// Wandelt die rohen REST-API-Match-Objekte (Odds-System-Struktur mit viel Altlast) in unser
// einfaches, flaches Format um. Komplett Vue-unabhängig, isoliert testbar.

import { ergebnisAusOddsMatchScores } from './liveMessage.js'

const STOP = new Set(["fc","sk","sv","fk","ac","as","afc","1","tsg","vfb","vfl","rb","ssc","us","cf"]);
const teamKey = s => (s ?? "").toLowerCase()
  .replace(/[^a-z0-9äöüß ]+/g, " ").split(/\s+/)
  .filter(t => t && !STOP.has(t)).join(" ");
const gleich = (a, b) => {
  const x = teamKey(a), y = teamKey(b);
  return !!x && !!y && (x === y || x.includes(y) || y.includes(x));
};

// Parst einen JSON-String sicher (die API verschachtelt öfter JSON-als-String) -> null bei Fehler/kein String
function parseJsonSicher(str) {
  if (typeof str !== 'string') return null
  try { return JSON.parse(str) } catch { return null }
}

// scores.scores ist ein Array von {name, score}-Objekten, immer in der Reihenfolge [Heim,
// Auswärts] - genau wie odds.homeTeam/awayTeam. GEÄNDERT: Namensvergleich komplett entfernt
// (funktionierte nicht zuverlässig - die Datenquelle nutzt teils UNTERSCHIEDLICHE Namens-
// Varianten zwischen den Feldern, z.B. odds.awayTeam = "Rapid Wien", aber im scores-Array
// stand "Rapid Vienna" - ein exakter String-Vergleich schlug dadurch fehl und das Ergebnis
// blieb leer, obwohl es in den Rohdaten längst vorhanden war). Stattdessen wird jetzt einfach
// die POSITION im Array genutzt (Index 0 = Heim, Index 1 = Auswärts) - keine Namens-Abhängigkeit
// mehr, kein Rateaufwand, kein Fallback nötig.
export function ausScoreListe(scoreListe) {
  if (!Array.isArray(scoreListe) || scoreListe.length !== 2) return null
  const h = Number(scoreListe[0]?.score)
  const a = Number(scoreListe[1]?.score)
  return { heimtore: Number.isNaN(h) ? null : h, austore: Number.isNaN(a) ? null : a }
}

function ermittleErgebnis(w, odds, istBeendet, istLive) {
  // Vorher wurde hier sofort aufgegeben, wenn das Spiel nicht "beendet" war - dadurch blieb der
  // Spielstand bei laufenden Spielen bis zum ersten WebSocket-Update leer, obwohl dieselben
  // REST-Felder (jsonMatch/scores/results) oft schon während des Spiels befüllt sind, nicht nur
  // danach. Jetzt auch bei laufenden Spielen versuchen, bevor der WebSocket überhaupt was liefert.
  if (!istBeendet && !istLive) return { heimtore: null, austore: null }

  // 1. odds.jsonMatch (Football-Data-Fixture-Format) -> goals.home/away, sauberste Quelle mit echten Zahlen
  const jsonMatch = parseJsonSicher(odds.jsonMatch)
  if (jsonMatch?.goals && typeof jsonMatch.goals.home === 'number') {
    return { heimtore: jsonMatch.goals.home, austore: jsonMatch.goals.away }
  }

  // 2. scores.scores -> Array von {name, score}, per Position zugeordnet (siehe ausScoreListe oben)
  const ausTopLevel = ausScoreListe(w.scores?.scores)
  if (ausTopLevel) return ausTopLevel

  // 3. odds.results (selbst wieder ein JSON-String) -> enthält ebenfalls ein scores-Array
  const results = parseJsonSicher(odds.results)
  const ausResults = ausScoreListe(results?.scores)
  if (ausResults) return ausResults

  return { heimtore: null, austore: null }
}

// best1/best2/bestDraw sind Objekte {name, price}, keine reinen Zahlen -> .price rausziehen
function ermittleQuoten(w) {
  const v = w.valueEM2024
  const preis = (feld) => {
    if (feld && typeof feld === 'object' && typeof feld.price === 'number') return feld.price
    if (typeof feld === 'number') return feld
    return null
  }
  // price: 0 in valueEM2024 ist ein Platzhalter/kein echter Wert -> nur > 0 als gültige Quote zählen
  return {
    quote1: preis(w.best1) ?? (v?.home?.price > 0 ? v.home.price : null),
    quoteX: preis(w.bestDraw) ?? (v?.draw?.price > 0 ? v.draw.price : null),
    quote2: preis(w.best2) ?? (v?.away?.price > 0 ? v.away.price : null),
  }
}

// Liga-Name allein reicht nicht als Schlüssel - "Bundesliga" gibt's z.B. in Deutschland UND Österreich.
// -> eindeutiger Schlüssel über die numerische Liga-ID (falls vorhanden), Anzeige-Label mit Land
//    ergänzt, sobald der Name mehrdeutig sein könnte.
// Leitet eine kurze Bezeichnung ab (z.B. "CL", "BL", "PL") - bevorzugt sport.captionshort
// (die kennt ihr ja schon so), sonst grobe Initialen aus dem vollen Namen als Fallback.
function ermittleLigaKurz(sport, vollerName) {
  if (sport.captionshort) return sport.captionshort
  const woerter = (vollerName ?? '').split(/\s+/).filter(w => w.length > 2 && w.toUpperCase() !== 'UEFA')
  return woerter.map(w => w[0]).join('').toUpperCase().slice(0, 3) || '?'
}

// Liga-Info (Name/Land/Logo/Flagge) kommt aus w.matchJson.league - liefert direkt ein sauberes
// Objekt (kein verschachtelter JSON-String nötig):
//   { id, name, country, logo, flag, season, round, standings }
// - logo: Liga-/Wettbewerbs-Logo (z.B. Bundesliga-Ball, Champions-League-Kugel)
// - flag: Länderflagge, bereits eine VOLLSTÄNDIGE URL (kein relativer Pfad)
function  ermittleLigaInfo(w, sport) {
  const liga = w.matchJson?.league
  if (liga?.name) {
    // BUGFIX: id muss IMMER unser eigener Primärschlüssel (sport.id) sein, nicht liga.id (die
    // API-eigene League-ID) - beide sind komplett unterschiedliche Nummernkreise. ligaId wird
    // u.a. fürs Tabellen-Matching genutzt (tabelleFuerLiga vergleicht gegen block.id, unseren
    // Primärschlüssel) - mit liga.id hätte das zufällig auf eine komplett falsche Liga treffen
    // können, sobald die API-ID zufällig mit dem Primärschlüssel einer ANDEREN Liga übereinstimmt.
    const label = sport.caption ?? liga.name
    const key = `liga-${sport.id}`
    return {
      key,
      label,
      id: sport.id ?? null,
      kurz: ermittleLigaKurz(sport, label),
      land: liga.country ?? null,
      logo: liga.logo ?? null,
      flagge: liga.flag ?? null
    }
  }
  const label = sport.captionshort || sport.caption || 'Unbekannt'
  return { key: `sport-${sport.id}`, label, id: sport.id ?? null, kurz: ermittleLigaKurz(sport, label), land: null, logo: null, flagge: null }
}

// Robust gegen beide Fälle: Backend liefert das Feld als bereits geparstes Objekt ODER als
// JSON-String (je nachdem wie der LONGTEXT auf dem Weg zum Client (de)serialisiert wird).
export function parseJsonFeld(feld, fallback) {
  if (feld == null) return fallback
  if (typeof feld === 'object') return feld
  try {
    return JSON.parse(feld)
  } catch {
    return fallback
  }
}

export function mapH2H(rohListe) {
  if (!Array.isArray(rohListe)) return []
  return rohListe
    .filter(f => f.goals?.home != null && f.goals?.away != null)
    .map(f => ({
      datumRoh: f.fixture?.date ?? null,
      datum: f.fixture?.date ? new Date(f.fixture.date).toLocaleDateString('de-DE') : '',
      liga: f.league?.name ?? '',
      stadt: f.fixture?.venue?.city ?? '',
      stadion: f.fixture?.venue?.name ?? '',
      heim: f.teams?.home?.name ?? '',
      auswaerts: f.teams?.away?.name ?? '',
      toreHeim: f.goals.home,
      toreAuswaerts: f.goals.away
    }))
    .sort((a, b) => new Date(b.datumRoh) - new Date(a.datumRoh))
}

export function mapTeamSpiel(w) {
  const odds = w.oddsSportsOdds ?? {}
  const sport = w.oddsSport ?? {}
  const istBeendet = odds.completed === true
  const min = odds.time != null ? (Date.now() - new Date(odds.time).getTime()) / 60000 : null
  const istLive = !istBeendet && min != null && min >= 0 && min <= 240
  const { heimtore, austore } = ermittleErgebnis(w, odds, istBeendet, istLive)
  const ligaInfo = ermittleLigaInfo(w, sport)
  const oms = istLive ? parseJsonFeld(odds.resultsLive, null) : null
  const liveStand = oms ? ergebnisAusOddsMatchScores(oms) : null

  return {
    id: Number(odds.id),
    zeit: odds.time,
    heim: odds.homeTeam,
    auswaerts: odds.awayTeam,
    heimId: odds.homeTeamID ?? null,
    auswaertsId: odds.awayTeamID ?? null,
    ligaFlaggeBild: ligaInfo.flagge ?? null,
    liga: ligaInfo.label || sport.caption || '',
    ligaKurz: ligaInfo.kurz || sport.caption || '',
    status: istBeendet ? 'beendet' : (istLive ? 'live' : 'geplant'),
    heimtore,
    austore,
    hatVorbericht: !!odds.facts_preview,
    hatBericht: !!odds.facts_report,
    heimLogo: w.matchJson?.home?.logo ?? (odds.homeTeamID ? `https://media.api-sports.io/football/teams/${odds.homeTeamID}.png` : null),
    auswaertsLogo: w.matchJson?.away?.logo ?? (odds.awayTeamID ? `https://media.api-sports.io/football/teams/${odds.awayTeamID}.png` : null),
    ligaLogo: ligaInfo.logo ?? w.matchJson?.league?.logo ?? null,
    heimtore: liveStand?.heimtore ?? heimtore,
    austore: liveStand?.austore ?? austore,
    minute: oms?.status?.elapsed ?? null,
    spielUhrKurz: oms?.status?.shortS ?? null,
    spielUhrExtra: oms?.status?.extra ?? null,
  }
}

export function mapMatch(w, flaggenBasisUrl = '') {
  const odds = w.oddsSportsOdds ?? {}
  const sport = w.oddsSport ?? {}
  const istBeendet = odds.completed === true
  // Live-Erkennung sofort beim Laden (noch bevor die WebSocket-Verbindung steht): Anstoßzeit
  // liegt in der Vergangenheit (aber nicht zu weit zurück - 4h deckt auch Verlängerung+Elfer
  // großzügig ab) und das Spiel ist noch nicht als beendet markiert -> vermutlich live. Die
  // WebSocket-Verbindung korrigiert das anschließend bei Bedarf (z.B. falls das Spiel tatsächlich
  // verschoben wurde). Die Obergrenze verhindert, dass alte Spiele ohne completed-Flag (Daten-
  // qualitätsproblem im Backend) für immer als "live" hängen bleiben.
  const minutenSeitAnstoss = odds.time != null ? (new Date().getTime() - new Date(odds.time).getTime()) / 60000 : null
  const istLive = !istBeendet && minutenSeitAnstoss != null && minutenSeitAnstoss >= 0 && minutenSeitAnstoss <= 240

  const { heimtore, austore } = ermittleErgebnis(w, odds, istBeendet, istLive)
  const { quote1, quoteX, quote2 } = ermittleQuoten(w)
  const ligaInfo = ermittleLigaInfo(w, sport)

  // w.flag liefert (in der ALTEN Quelle) ein SVG-Bild statt Emoji-Zuordnung, z.B.
  // { caption:"England", path:"/css/flags/4x3/gb-eng.svg" } - der Pfad ist relativ, deshalb mit
  // der Basis-URL (Herkunft von api-base) zur vollständigen URL zusammensetzen. Als FALLBACK
  // beibehalten, falls w.matchJson.league.flag (neue, bevorzugte Quelle) mal fehlen sollte.
  const alteFlaggeFallback = w.flag?.path ? `${flaggenBasisUrl}${w.flag.path}` : null


  const roh = parseJsonFeld(odds.facts_preview, null);
  const facts = roh?.facts ?? roh ?? {};            // beide Formen akzeptieren
  const neu = facts.recherche ? facts : null;       // neues Facts-Objekt
  const heimName = neu?.heim?.name;
  const auswName = neu?.auswaerts?.name;

  const rohReport = parseJsonFeld(odds.facts_report, null);
  const factsReport = rohReport?.facts ?? rohReport ?? null;
  const faktenNachSpiel = factsReport?.recherche?.weitereFakten ?? [];

  const alle = neu?.recherche?.ausfaelle ?? [];
  const fuerSeite = (s, name) =>
    alle.filter(a => a.seite ? a.seite === s : gleich(a.team, name));   // alte Einträge: Namensvergleich

  const ausfaelleHeim = fuerSeite("heim", heimName);
  const ausfaelleAuswaerts = fuerSeite("auswaerts", auswName);

  const fehlt = alle.filter(a => !a.seite);
  if (fehlt.length) {
    //console.log("[ausfaelle] ohne seite:", fehlt.map(a => a.team + ": " + a.name),
    //            "| heim=", heimName, "auswaerts=", auswName);
  }

  const research = neu
    ? { ausfaelleHeim, ausfaelleAuswaerts, weitereFakten: neu.recherche.weitereFakten ?? [] }
    : (facts.research ?? null);                      // alte Zeilen bleiben lesbar

  const faktencheck = neu
    ? { spielort: neu.spielort?.name ?? null, hinspiel: neu.hinspiel ?? null }
    : (facts.faktencheck ?? null);


    return {
    id: Number(odds.id),
    zeit: odds.time,
    heim: odds.homeTeam,
    auswaerts: odds.awayTeam,
    heimId: w.matchJson?.home?.id ?? null,
    auswaertsId: w.matchJson?.away?.id ?? null,
    heimLogo: w.matchJson?.home?.logo ?? null,
    auswaertsLogo: w.matchJson?.away?.logo ?? null,
    liga: ligaInfo.label,      // Anzeige-Text (z.B. "Bundesliga (Austria)")
    ligaKey: ligaInfo.key,     // eindeutiger Schlüssel fürs Filtern/Gruppieren
    ligaId: ligaInfo.id,       // numerische ID fürs Debug-Attribut
    ligaKurz: ligaInfo.kurz,   // z.B. "CL", "BL1" - fürs Kalender-Badge
    ligaLand: ligaInfo.land,   // z.B. "Austria" - für den Tooltip/Emoji-Fallback
    ligaFlaggeBild: ligaInfo.flagge ?? alteFlaggeFallback,
    ligaLogo: ligaInfo.logo,   // Liga-Logo-URL
    status: istBeendet ? 'beendet' : (istLive ? 'live' : 'geplant'),
    minute: null, // wird per WebSocket befüllt (status.elapsed)
    spielUhrKurz: null, // 'HT', '1H', '2H' usw. - wird per WebSocket befüllt (status.shortS)
    spielUhrExtra: null, // Nachspielzeit in Minuten - wird per WebSocket befüllt (status.extra)
    heimtore,
    austore,
    quote1,
    quoteX,
    quote2,
    // lineups/events/statistiken kommen NICHT mehr im großen Spielplan-Request mit (abgespeckte
    // Match-Klasse - bei vielen Spielen war das zu viel Datenvolumen). Der Server liefert
    // stattdessen nur noch Flags, ob's überhaupt welche gibt - Buttons/Tabs schalten anhand
    // dieser Flags, die eigentlichen Daten werden erst bei Bedarf lazy nachgeladen (siehe
    // lib/matchDetails.js + DetailOverlay.vue).
    hatLineup: !!w.hasLineup,
    hatEvents: !!w.hasEvents,
    hatStatistik: !!w.hasStatistics,
    lineups: Array.isArray(w.lineups) ? w.lineups : [],       // bleibt leer, bis lazy nachgeladen
    statistiken: Array.isArray(w.statistics) ? w.statistics : [],
    events: Array.isArray(w.events) ? w.events : [],
    // Story: preview UND report jetzt GETRENNT gehalten (statt nur "die aktuell relevante"), da
    // beide unabhängig voneinander als eigener Button/Tab zugänglich sein sollen (Vorschau bleibt
    // auch nach Spielende sichtbar, Spielbericht kommt als eigener, separater Button dazu).
    // Werden in useSpielplanData.js aus raw.stories nachträglich befüllt (dort per matchId
    // zugeordnet) - hier nur als leere Platzhalter deklariert.
    storyPreview: null,
    storyDatenPreview: null,
    storyReport: null,
    storyDatenReport: null,
    ausfaelle: {
      heim: research?.ausfaelleHeim ?? [],
      auswaerts: research?.ausfaelleAuswaerts ?? []
    },
    weitereFakten: research?.weitereFakten ?? [],
    weitereFaktenReport: faktenNachSpiel,
    erstelltAm: neu?.erstelltAm ?? null,
    erstelltAmReport: factsReport?.erstelltAm ?? null,
    hatRecherche: !!research,
    faktencheck: faktencheck ?? null,
    h2h: mapH2H(parseJsonFeld(w.head2head, []))
  }
}

// Die API liefert ein Objekt { "<datum>": [match, match, ...], ... } statt eines flachen Arrays -> flachklopfen + mappen
export function normalizeApiResponse(raw, flaggenBasisUrl = '') {
  const alle = []
  for (const spieleAmTag of Object.values(raw ?? {})) {
    for (const wrapper of spieleAmTag) {
      alle.push(mapMatch(wrapper, flaggenBasisUrl))
    }
  }
  return alle
}